<script setup lang="ts">
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useUserStore } from '@/store/userStore'
  import {
    AIChatSessionTypeEnum,
    type AIChatSession,
    type CustomerCase,
    type CustomerOrganisationUser,
    type SessionGroup
  } from '@/types/generated'
  import { AiChatSessionsService } from '@/types/generated/services.gen'
  import * as Sentry from '@sentry/vue'
  import {
    ChevronRight,
    Download,
    FolderOpen,
    FolderPlus,
    FolderSymlink,
    MoreHorizontal,
    Paperclip,
    Redo2,
    Trash2
  } from 'lucide-vue-next'
  import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRoot,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
  } from 'radix-vue'
  import { computed, inject, ref, type PropType, type Ref } from 'vue'

  const props = defineProps({
    chatSession: {
      type: Object as PropType<AIChatSession>,
      required: true
    },
    inFolder: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits<{
    click: [session: AIChatSession]
    delete: [session: AIChatSession]
    moveToFolder: [session: AIChatSession, folder: SessionGroup | null]
    createFolder: [session: AIChatSession]
    select: [session: AIChatSession]
  }>()

  const { t } = useTranslation({
    en: {
      EmptyMessage: 'No messages',
      DownloadChatAsPdf: 'Download Chat as PDF',
      AttachToCaseAsPdf: 'Attach to Case as PDF',
      DeleteChat: 'Delete Chat',
      MoveToFolder: 'Move to folder',
      NewFolder: 'New Folder',
      RemoveFromFolder: 'Remove from folder'
    },
    de: {
      EmptyMessage: 'Keine Nachrichten',
      DownloadChatAsPdf: 'Chat als PDF herunterladen',
      AttachToCaseAsPdf: 'An Fall als PDF anhängen',
      DeleteChat: 'Chat löschen',
      MoveToFolder: 'In Ordner verschieben',
      NewFolder: 'Neuer Ordner',
      RemoveFromFolder: 'Aus Ordner entfernen'
    }
  })

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const active = computed(() => activeChatSession.value?.uuid === props.chatSession.uuid)
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const caseStore = useCaseStore()
  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()

  const user = computed<CustomerOrganisationUser | null>(() => {
    // For case context, get user from case data
    if (sessionType === AIChatSessionTypeEnum.CASE && userStore.user?.org?.users) {
      return (
        userStore.user.org.users.find((user) => user.uuid === props.chatSession.started_by_user) ||
        null
      )
    }

    // For global context, get user from organization users in global store
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      return globalChatStore.getOrganisationUserByUuid(props.chatSession.started_by_user)
    }

    return null
  })
  const firstUserMessage = computed(() => {
    const userMessages = props.chatSession.messages
      .filter((msg) => msg.role === 'user')
      .sort(
        (a, b) => new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime()
      )
    return userMessages[0]?.content || t('EmptyMessage')
  })

  const lastUserMessage = computed(() => {
    const userMessages = props.chatSession.messages
      .filter((msg) => msg.role === 'user')
      .sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
      )
    return userMessages[0]?.content || t('EmptyMessage')
  })

  const truncatedFirstMessage = computed(() => {
    const msg = firstUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })

  const truncatedLastMessage = computed(() => {
    const msg = lastUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })

  const isGeneratingPdf = ref(false)

  // Get available folders for moving chat sessions
  const availableFolders = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      return globalChatStore.sessionGroups.filter(
        (folder) => folder.uuid !== props.chatSession.session_group
      )
    }
    return []
  })

  const handleClick = () => {
    emit('click', props.chatSession)
  }

  const handleDelete = () => {
    emit('delete', props.chatSession)
  }

  const handleMoveToFolder = async (folder: SessionGroup | null) => {
    try {
      // Prepare the update data
      const updateData: any = {
        session_group_uuid: folder ? folder.uuid : null
      }

      // If moving to a folder and the current title is "New Chat", update it to use the first message
      if (folder && (!props.chatSession.title || props.chatSession.title.trim() === 'New Chat')) {
        const firstMessage = firstUserMessage.value
        if (firstMessage && firstMessage !== t('EmptyMessage')) {
          updateData.title =
            firstMessage.length > 50 ? firstMessage.substring(0, 50) + '...' : firstMessage
        }
      }

      // Update the chat session's folder via API using PATCH
      const updatedSession = await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid: props.chatSession.uuid,
        requestBody: updateData
      })

      // Update the session in the global store
      const globalChatStore = useGlobalChatStore()
      globalChatStore.updateChatSession(updatedSession)

      // Emit the event to parent components
      emit('moveToFolder', updatedSession, folder)

      // Select the moved chat session
      emit('select', updatedSession)
    } catch (error) {
      console.error('Failed to move chat to folder:', error)
    }
  }

  const handleCreateFolder = (event?: Event) => {
    // Prevent the click event from bubbling up
    if (event) {
      event.stopPropagation()
    }
    emit('createFolder', props.chatSession)
  }

  const downloadChatAsPdf = async (attachToCase: boolean) => {
    try {
      isGeneratingPdf.value = true

      const sessionId = props.chatSession.uuid
      if (!sessionId) {
        genericErrorToast()
        return
      }

      // Use the generated service method
      const response = await AiChatSessionsService.aiChatSessionsExportPdfCreate({
        uuid: sessionId,
        requestBody: {
          attach_to_case: attachToCase,
          message_uuid: null // Export entire chat
        }
      })

      if (response.success) {
        if (attachToCase) {
          // For documents attached to case, use the URL
          if (response.url) {
            const link = document.createElement('a')
            link.href = response.url
            link.download = `chat-${sessionId.substring(0, 8)}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Refresh case data silently to show the new document (only for case context)
            if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.uuid) {
              await caseStore.fetchCaseWithErrorHandling(caseData.value.uuid)
            }
          }
        } else {
          // For temporary files, use the content_base64 data
          if (response.content_base64) {
            // Convert base64 string to binary data using atob()
            const binaryString = atob(response.content_base64)

            // Create a Uint8Array from the binary string
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }

            const blob = new Blob([bytes], {
              type: 'application/pdf'
            })

            // Create a download link
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = response.filename || `chat-${sessionId.substring(0, 8)}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          }
        }

        saveSuccessToast()
      } else {
        genericErrorToast()
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      genericErrorToast()
      Sentry.captureException(error)
    } finally {
      isGeneratingPdf.value = false
    }
  }
</script>
<template>
  <div
    v-if="user"
    class="relative cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
      dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
    :class="[
      active ? 'border-r-2 border-blue-500 bg-gray-200' : 'bg-white hover:bg-gray-100',
      inFolder ? 'w-full' : ''
    ]"
    data-cy="chat-session-item"
    @click="handleClick"
  >
    <div
      :class="[
        'flex items-center gap-x-3',
        inFolder
          ? 'gap-2 py-3 pl-4 pr-2'
          : 'border-b border-b-gray-100 px-5 py-4 dark:border-neutral-700'
      ]"
    >
      <div class="shrink-0">
        <div class="relative size-8">
          <UserAvatar
            :user="user"
            size="32px"
          />
        </div>
      </div>
      <div
        :class="
          inFolder
            ? `flex min-h-px min-w-px shrink-0 grow basis-0 flex-col content-stretch items-start text-nowrap
              not-italic`
            : 'grow truncate'
        "
      >
        <div
          :class="
            inFolder
              ? 'relative flex w-full shrink-0 content-stretch items-baseline justify-between'
              : 'flex items-center justify-between gap-x-1'
          "
        >
          <div
            :class="
              inFolder
                ? `relative min-h-px min-w-px shrink-0 grow basis-0 overflow-hidden overflow-ellipsis text-sm
                  font-semibold text-gray-800`
                : 'flex-1 truncate'
            "
          >
            <p
              :class="
                inFolder
                  ? 'overflow-hidden overflow-ellipsis text-nowrap text-sm font-medium leading-5 text-gray-800'
                  : 'truncate text-sm font-medium text-gray-800 dark:text-neutral-200'
              "
            >
              {{ user.first_name }} {{ user.last_name }}
            </p>
          </div>
          <div
            :class="
              inFolder
                ? 'relative shrink-0 text-xs font-normal text-gray-400'
                : 'flex items-center gap-2'
            "
          >
            <span
              v-if="isSessionGenerating(chatSession.uuid)"
              class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"
            ></span>
            <p
              :class="
                inFolder
                  ? 'whitespace-pre text-nowrap text-xs leading-4 text-gray-400'
                  : 'text-xs uppercase text-gray-400 dark:text-neutral-500'
              "
            >
              {{ new Date(chatSession.created_at).toLocaleDateString('de-DE') }}
            </p>
          </div>
          <!-- Options Menu Button -->
          <DropdownMenuRoot>
            <DropdownMenuTrigger as-child>
              <button
                class="relative shrink-0 cursor-pointer rounded-md bg-white"
                @click.stop
              >
                <div
                  class="relative box-border flex size-full content-stretch items-center justify-center overflow-clip p-1.5"
                >
                  <div class="relative size-5 shrink-0 overflow-clip">
                    <MoreHorizontal class="h-5 w-5 text-gray-800" />
                  </div>
                </div>
                <div
                  aria-hidden="true"
                  class="pointer-events-none absolute inset-0 rounded-md border border-solid border-gray-200
                    shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
                />
                <span class="sr-only">Open menu</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="right"
              class="z-50 w-[220px] rounded-[12px] border border-gray-200 bg-white p-[6px] shadow-lg"
            >
              <DropdownMenuItem
                class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                  text-gray-800 hover:bg-gray-50"
                :disabled="isGeneratingPdf"
                @click.stop="downloadChatAsPdf(false)"
              >
                <Download class="h-5 w-5 text-gray-800" />
                <span class="text-sm leading-5">{{ t('DownloadChatAsPdf') }}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="sessionType === AIChatSessionTypeEnum.CASE"
                class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                  text-gray-800 hover:bg-gray-50"
                :disabled="isGeneratingPdf"
                @click.stop="downloadChatAsPdf(true)"
              >
                <Paperclip class="h-5 w-5 text-gray-800" />
                <span class="text-sm leading-5">{{ t('AttachToCaseAsPdf') }}</span>
              </DropdownMenuItem>

              <!-- Move to folder submenu (only for global context) -->
              <template v-if="sessionType === AIChatSessionTypeEnum.GLOBAL">
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                      text-gray-800 hover:bg-gray-50"
                  >
                    <FolderSymlink class="h-5 w-5 text-gray-800" />
                    <span class="flex-1 text-sm leading-5">{{ t('MoveToFolder') }}</span>
                    <ChevronRight class="h-5 w-5 text-gray-800" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    class="z-50 w-[220px] rounded-[12px] border border-gray-200 bg-white p-[6px] shadow-lg"
                    align="start"
                    :side-offset="2"
                  >
                    <DropdownMenuItem
                      class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                        text-gray-800 hover:bg-gray-50"
                      @click="handleCreateFolder"
                    >
                      <FolderPlus class="h-5 w-5 text-blue-500" />
                      <span class="text-sm leading-5">{{ t('NewFolder') }}</span>
                    </DropdownMenuItem>
                    <template v-if="availableFolders.length > 0">
                      <DropdownMenuSeparator class="my-1" />
                      <DropdownMenuItem
                        v-for="folder in availableFolders"
                        :key="folder.uuid"
                        class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                          text-gray-800 hover:bg-gray-50"
                        :title="folder.name"
                        @click.stop="handleMoveToFolder(folder)"
                      >
                        <FolderOpen class="h-5 w-5 shrink-0 text-gray-800" />
                        <span class="truncate text-sm leading-5">{{ folder.name }}</span>
                      </DropdownMenuItem>
                    </template>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </template>

              <!-- Remove from folder (only for global context and only if session is in a folder) -->
              <template
                v-if="sessionType === AIChatSessionTypeEnum.GLOBAL && chatSession.session_group"
              >
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                    text-gray-800 hover:bg-gray-50"
                  @click.stop="handleMoveToFolder(null)"
                >
                  <Redo2 class="h-5 w-5 text-gray-800" />
                  <span class="text-sm leading-5">{{ t('RemoveFromFolder') }}</span>
                </DropdownMenuItem>
              </template>
              <DropdownMenuSeparator class="my-1" />
              <DropdownMenuItem
                class="flex w-full cursor-pointer items-center gap-[6px] rounded-[6px] p-[6px] text-sm font-medium
                  text-red-600 hover:bg-red-50"
                @click.stop="handleDelete"
              >
                <Trash2 class="h-5 w-5 text-red-600" />
                <span class="text-sm leading-5">{{ t('DeleteChat') }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        </div>
        <div
          :class="
            inFolder
              ? 'relative w-full shrink-0 overflow-hidden overflow-ellipsis text-sm font-normal text-gray-500'
              : 'flex items-center justify-between'
          "
        >
          <div :class="inFolder ? '' : 'me-5 flex-1 truncate'">
            <p
              :class="
                inFolder
                  ? 'overflow-hidden overflow-ellipsis text-nowrap text-sm leading-5 text-gray-500'
                  : 'truncate text-sm text-gray-500 dark:text-neutral-500'
              "
            >
              {{
                inFolder ? chatSession.title?.trim() || truncatedFirstMessage : truncatedLastMessage
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
